import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordInputWithValidation } from '@features/onboarding/components/shared/PasswordInputWithValidation';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useRegister } from '@features/auth/lib/useRegister';
import { Form, FormField, FormItem, FormControl } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';

export const RegisterSchema = z.object({
  firstName: z.string()
    .min(1, 'First name is required.')
    .refine((val) => val.trim().length > 0, 'First name cannot be only spaces.')
    .refine((val) => !val.startsWith(' '), 'First name cannot start with a space.'),
  lastName: z.string()
    .min(1, 'Last name is required.')
    .refine((val) => val.trim().length > 0, 'Last name cannot be only spaces.')
    .refine((val) => !val.startsWith(' '), 'Last name cannot start with a space.'),
  email: z.string().email('Please enter a valid email address.'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long.')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .regex(/[0-9]/, 'Password must contain at least one number.')
    .regex(/[^A-Za-z0-9\s]/, 'Password must contain at least one special character.'),
});

type RegisterFormData = z.infer<typeof RegisterSchema>;

const sanitizeNameInput = (value: string) =>
  value
    .replace(/[^A-Za-zéñüóíáúèàùëïöäüÿçñ\s\-'\.]/g, '')
    .replace(/<[^>]*>/gi, '')
    .replace(/script|javascript/gi, '')
    .replace(/\s+/g, ' ');

export const RegistrationForm: React.FC = () => {
  const { register, error, isLoading, clear } = useRegister();
  const [ disabledSubmit, setDisabledSubmit ] = useState(false);
  const form = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // mode: 'onChange', // Enable real-time validation
  });

  const {
    formState: { errors },
    clearErrors,
  } = form;

  const handleNameChange = (
    fieldName: 'firstName' | 'lastName',
    value: string
  ) => {
    const sanitizedValue = sanitizeNameInput(value);
    form.setValue(fieldName, sanitizedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    clearErrors(fieldName);
  };

  const onSubmit = async (data: RegisterFormData) => {
    const trimmedData = {
      ...data,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
    };
    await register(trimmedData);
  };

  useEffect(() => {
    // Блокируем submit только при ошибках валидации формы
    const hasFormErrors = Object.values(errors).length > 0;
    setDisabledSubmit(hasFormErrors);
  }, [JSON.stringify(errors)]);

  // Clear error when email or password changes
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');
  const email = form.watch('email');
  const password = form.watch('password');

  useEffect(() => {
    clear();
  }, [firstName, lastName, email, password, clear]);

  return (
    <AuthLayout
      title="Welcome!"
      description="New here? You’re in the right spot. We're excited to help you explore insurance options that fit your life and budget. To get started, simply create an account—this keeps your information secure and lets you save your progress along the way. Let’s find the coverage that works for you!"
      variant="register"
      titleClassName="text-[40px] max-md:text-[28px]"
      descriptionClassName="text-xl mt-4.5 leading-7 max-md:text-[14px] max-md:leading-5"
      headerClassName="2xl:pt-19.5 2xl:pl-6.5 max-md:pb-37"
      contentClassName="2xl:pt-22.5 2xl:pl-24"
    >
      <div className="w-full max-w-[450px]">
        <section className="max-w-110">
          <h1 className="font-extrabold text-4xl mb-6">Create an account here!</h1>
          {/* <h1 className="font-extrabold text-4xl mb-2">Let’s get started!</h1> */}
          {/* <p className="text-[14px] max-w-[430px] max-sm:max-w-87 flex-wrap !font-secondary">
            If you’re new here, just take a moment to create an account—it’s
            quick, easy, and lets you securely save your progress so you can
            return anytime. Already started an application? Simply log in to
            pick up right where you left off. The whole process is
            straightforward and usually takes less than 10 minutes!
          </p> */}
        </section>
        {/* <h3 className="text-xl font-extrabold mb-6 mt-8">
          New user? Create an account here:
        </h3> */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="max-w-[430px]"
          >
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingInput
                        id="firstName"
                        label="First name"
                        type="text"
                        value={field.value}
                        onChange={(e) =>
                          handleNameChange('firstName', e.target.value)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputClass="w-full max-w-107.5 mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                      />
                    </FormControl>
                    {fieldState.error && (
                      <p className="text-primary text-sm -mt-4 mb-4">
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingInput
                        id="lastName"
                        label="Last name"
                        type="text"
                        value={field.value}
                        onChange={(e) =>
                          handleNameChange('lastName', e.target.value)
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        inputClass="w-full max-w-107.5 mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                      />
                    </FormControl>
                    {fieldState.error && (
                      <p className="text-primary text-sm -mt-4 mb-4">
                        {fieldState.error.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="email"
                      label="Email address"
                      type="email"
                      value={field.value}                      
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('email');
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputClass="w-full max-w-107.5 mb-5 h-16 rounded-lg border border-[#B7B7B7] p-2 disabled:text-[#ffffff55] disabled:opacity-100"
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-primary text-sm -mt-4 mb-4">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />

            <PasswordInputWithValidation
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              isSubmitDisabled={disabledSubmit || isLoading} 
            >
              {error && (
                <p className="mt-4 -mb-4 text-primary text-sm text-center">{error}</p>
              )}
            </PasswordInputWithValidation>

            <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
              Returning user?{' '}
              <Link
                to="/login"
                className="text-primary hover:underline whitespace-nowrap"
              >
                Click here to log in.
              </Link>
            </p>
          </form>
        </Form>
      </div>
    </AuthLayout>
  );
};
